import React from 'react';
import styled from 'styled-components';
import { Student, ScannedData } from '../types';
import { YoklaAssignment } from '../types';

interface StudentCardProps {
  student: Student | ScannedData;
  isActive: boolean;
  isCompleted?: boolean;
  onClick: () => void;
  isApellyasiyaMode?: boolean;
  studentNumber?: number;        // NEW
  score?: number | null;         // NEW
  questionNumber?: string;       // NEW
  assignment?: YoklaAssignment;  // NEW
}

const Card = styled.div<{ $isActive: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${props => props.$isActive ? '#3b82f6' : '#e2e8f0'};
  box-shadow: ${props => props.$isActive
    ? '0 4px 12px rgba(59, 130, 246, 0.15)'
    : '0 2px 8px rgba(0, 0, 0, 0.1)'
  };

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StudentName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const StudentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #64748b;
  font-size: 14px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StatusBadge = styled.div<{ $isActive: boolean; $isCompleted?: boolean }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    if (props.$isCompleted) return '#10b981';
    if (props.$isActive) return '#dbeafe';
    return '#f1f5f9';
  }};
  color: ${props => {
    if (props.$isCompleted) return 'white';
    if (props.$isActive) return '#1d4ed8';
    return '#64748b';
  }};
  margin-top: 8px;
`;

// NEW: Student number badge
const StudentNumber = styled.div<{ $clickable: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$clickable
    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    : '#f1f5f9'};
  color: ${props => props.$clickable ? 'white' : '#64748b'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${props => props.$clickable && `
    &:hover {
      transform: scale(1.1);
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
    }
  `}
`;

// NEW: Score display badge
const ScoreDisplay = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
`;

const CardLayout = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const StudentCard: React.FC<StudentCardProps> = ({
  student,
  isActive,
  isCompleted,
  onClick,
  isApellyasiyaMode = false,
  studentNumber,
  score,
  questionNumber,
  assignment
}) => {
  // Adı gizle: Ali Hənsənov -> A**** ********
  const hideName = (name: string, surname: string) => {
    const hiddenName = name.charAt(0) + '*'.repeat(Math.max(1, name.length - 1));
    const hiddenSurname = '*'.repeat(Math.max(1, surname.length));
    return `${hiddenName} ${hiddenSurname}`;
  };

  // Öğrenci numarasını gizle: 250269 -> 2*****
  const hideStudentId = (studentId: string | number) => {
    const idStr = studentId.toString();
    if (idStr.length <= 2) return '*'.repeat(idStr.length);
    return idStr.charAt(0) + '*'.repeat(idStr.length - 1);
  };

  // ScannedData veya Student tipine göre değerleri al
  const getName = () => {
    if ('firstName' in student) {
      return hideName(student.firstName, student.lastName);
    }
    return hideName(student.name, student.surname);
  };

  const getStudentId = () => {
    if ('student_id' in student) {
      return hideStudentId(student.student_id);
    }
    return hideStudentId(student.studentId);
  };

  const getGrade = () => {
    if ('grade' in student) {
      return student.grade;
    }
    return student.sinif;
  };

  const getBlock = () => {
    if ('specialtyGroup' in student) {
      return student.specialtyGroup;
    }
    return student.blok;
  };

  return (
    <Card $isActive={isActive} onClick={onClick}>
      <CardLayout>
        {/* NEW: Student number badge */}
        {studentNumber !== undefined && (
          <StudentNumber
            $clickable={true}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            title={`Şagird ${studentNumber}`}
          >
            {studentNumber}
          </StudentNumber>
        )}

        <CardContent>
          <CardHeader>
            <StudentInfo>
              <StudentName>{getName()}</StudentName>
              <StudentDetails>
                {!isApellyasiyaMode && (
                <DetailItem>
                  <span>İştriakçı No:</span>
                  <span>{getStudentId()}</span>
                </DetailItem>
                )}
                <DetailItem>
                  <span>Sinif:</span>
                  <span>{getGrade()}</span>
                </DetailItem>
                <DetailItem>
                  <span>Qrup:</span>
                  <span>{getBlock()}</span>
                </DetailItem>
              </StudentDetails>
            </StudentInfo>

            {/* NEW: Score display badge */}
            {isCompleted && score !== null && score !== undefined && (
              <ScoreDisplay title="Qiymət">
                {score} bal
              </ScoreDisplay>
            )}
          </CardHeader>

          <StatusBadge $isActive={isActive} $isCompleted={isCompleted}>
            {isCompleted ? 'Tamamlandı ✓' : isActive ? 'Aktiv' : 'Gözləmədə'}
          </StatusBadge>
        </CardContent>
      </CardLayout>
    </Card>
  );
};

export default StudentCard;
